// Copyright (c) 2025 by Audere

import { capitalize } from "@mui/material";
import {
  LabelerConversationData,
  LabelerData,
  LabelingRole,
  QCConversationData,
  QCData,
} from "../api/labelApiSlice";
import { useConversationForLabeler } from "../../hooks/useConversationForLabeler";
import { useConversationForQC } from "../../hooks/useConversationForQC";
import { useConversationForMonitor } from "../../hooks/useConversationForMonitor";

/**
 * Filter conversations based on userSearch string
 * Matches UID, priority, or labeler email/name
 */
export function filterConversations(
  conversations: LabelerConversationData[] | QCConversationData[],
  userSearch: string
) {
  const searchInput = userSearch.toLowerCase();

  return conversations.filter((conversation) => {
    const uidMatch = conversation.conversation_uid
      .toLowerCase()
      .includes(searchInput);
    const priorityMatch = conversation.priority
      .toLowerCase()
      .includes(searchInput);
    const labelerMatch = conversation.labelers.some((labeler: LabelerData) =>
      [labeler.email, labeler.name].some((field) =>
        field.toLowerCase().includes(searchInput)
      )
    );

    return uidMatch || priorityMatch || labelerMatch;
  });
}

// Remove underscores from status and apply capitalization
export function formatStatusText(status: string): string {
  return status
    .split("_")
    .map((word) => (word === "qc" ? word.toUpperCase() : capitalize(word)))
    .join(" ");
}

export function getAnnotators(
  convo: QCConversationData | LabelerConversationData
) {
  return "qc" in convo ? convo.qc : convo.labelers;
}

export function getAnnotatorsForMonitor(
  convo: QCConversationData | LabelerConversationData
) {
  return Object.values(
    [...convo.labelers, ...("qc" in convo ? convo.qc : [])].reduce<
      Record<string, LabelerData | QCData>
    >((acc, user) => {
      acc[user.email] = user;
      return acc;
    }, {})
  );
}

export function getConversationHook(role: LabelingRole) {
  if (role === LabelingRole.conversation_labeler) {
    return useConversationForLabeler;
  } else if (role === LabelingRole.conversation_label_qc) {
    return useConversationForQC;
  } else if (role === LabelingRole.conversation_label_monitor) {
    return useConversationForMonitor;
  } else {
    throw new Error(`Invalid role: ${role}`);
  }
}
